import { useState } from 'react';
import { Box, ButtonBase, Dialog, IconButton, Link, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';
import PropTypes from 'prop-types';
import { DIAS_CONSERVACION, esImagen, urlOptimizada } from './adjuntos';

// Archivo que ya no existe: pasaron los días que se conservan. El mensaje mantiene su texto; aquí solo se avisa.
const ArchivoCaducado = ({ adjunto }) => (
  <Box
    data-testid="archivo-caducado"
    sx={{
      display: 'flex', alignItems: 'center', gap: 1, p: 1, maxWidth: 260, bgcolor: 'sutil',
      border: '1px dashed', borderColor: 'text.secondary', borderRadius: 2, color: 'text.secondary',
    }}
  >
    {esImagen(adjunto) ? <HideImageOutlinedIcon fontSize="small" /> : <PictureAsPdfOutlinedIcon fontSize="small" />}
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {adjunto.nombre || (esImagen(adjunto) ? 'Imagen' : 'Documento')}
      </Typography>
      <Typography variant="caption">Archivo eliminado: se conservan {DIAS_CONSERVACION} días</Typography>
    </Box>
  </Box>
);
ArchivoCaducado.propTypes = { adjunto: PropTypes.object.isRequired };

// Archivos de un mensaje dentro de la burbuja: las imágenes como miniaturas que se abren en grande y
// los PDF como tarjetas que se abren en otra pestaña. No muestra nada si el mensaje no tiene adjuntos.
const AdjuntosMensaje = ({ adjuntos }) => {
  const [visor, setVisor] = useState(null);
  if (!adjuntos || adjuntos.length === 0) return null;

  const caducados = adjuntos.filter((a) => a.caducado);
  const vigentes = adjuntos.filter((a) => !a.caducado);
  const imagenes = vigentes.filter(esImagen);
  const documentos = vigentes.filter((a) => !esImagen(a));
  const una = imagenes.length === 1;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
        {imagenes.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {imagenes.map((a, i) => (
              <ButtonBase
                key={`${a.publicId || a.url}-${i}`}
                onClick={() => setVisor(a)}
                aria-label={`Ver imagen ${a.nombre || ''}`.trim()}
                sx={{ display: 'block', borderRadius: 2, overflow: 'hidden', maxWidth: '100%' }}
              >
                <Box
                  component="img" src={a.miniatura || urlOptimizada(a.url, una ? 480 : 240)} alt={a.nombre || 'Imagen adjunta'} loading="lazy"
                  sx={{
                    display: 'block', maxWidth: '100%', objectFit: 'cover', bgcolor: 'sutil',
                    ...(una ? { width: 240, maxHeight: 300 } : { width: 112, height: 112 }),
                  }}
                />
              </ButtonBase>
            ))}
          </Box>
        )}

        {caducados.map((a, i) => <ArchivoCaducado key={`caducado-${a.publicId || a.nombre}-${i}`} adjunto={a} />)}

        {documentos.map((a, i) => (
          <Link
            key={`${a.publicId || a.url}-${i}`}
            href={a.url} target="_blank" rel="noopener noreferrer" underline="none"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, p: 1, maxWidth: 260,
              bgcolor: 'white', color: 'primary.main', border: 1, borderColor: 'divider', borderRadius: 2,
              '&:hover': { bgcolor: 'grey.50' },
            }}
          >
            <PictureAsPdfOutlinedIcon sx={{ color: 'error.oscuro', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.2 }}>{a.nombre || 'Documento'}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>PDF · Abrir</Typography>
            </Box>
          </Link>
        ))}
      </Box>

      <Dialog open={!!visor} onClose={() => setVisor(null)} maxWidth="lg">
        {visor && (
          <Box sx={{ position: 'relative', bgcolor: 'common.black', display: 'flex', flexDirection: 'column' }}>
            <Box
              component="img" src={visor.vista || urlOptimizada(visor.url, 1600)} alt={visor.nombre || 'Imagen adjunta'}
              sx={{ maxWidth: '92vw', maxHeight: '78vh', objectFit: 'contain' }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2, py: 1, color: 'white' }}>
              <Typography variant="body2" noWrap>{visor.nombre}</Typography>
              <Link href={visor.url} target="_blank" rel="noopener noreferrer" sx={{ color: 'white', whiteSpace: 'nowrap' }}>
                Abrir original
              </Link>
            </Box>
            <IconButton
              aria-label="Cerrar" onClick={() => setVisor(null)}
              sx={{ position: 'absolute', top: 4, right: 4, color: 'white', bgcolor: 'rgba(0,0,0,0.45)', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </Dialog>
    </>
  );
};

AdjuntosMensaje.propTypes = {
  adjuntos: PropTypes.array,
};

export default AdjuntosMensaje;
